/**
 * KDV Calculator Component
 * Handles Turkish VAT calculation (KDV Dahil/Hariç)
 */

class KdvCalculator {
  constructor() {
    this.form = document.getElementById('kdv-calculator-form');
    this.inputs = {
      amount: document.getElementById('kdv-amount'),
      customRate: document.getElementById('kdv-custom-rate')
    };
    
    // Radio buttons for calculation direction
    this.calculationDirection = {
      dahilToHaric: document.getElementById('kdv-dahil-to-haric'),
      haricToDahil: document.getElementById('kdv-haric-to-dahil')
    };
    
    // VAT rate buttons
    this.rateButtons = document.querySelectorAll('.kdv-rate-btn');
    this.customRateContainer = document.getElementById('custom-rate-container');
    
    // Action buttons
    this.calculateBtn = document.getElementById('calculate-kdv');
    this.resetBtn = document.getElementById('reset-form');
    
    // Result elements
    this.resultSection = document.getElementById('kdv-result');
    this.resultElements = {
      kdvHaric: document.getElementById('kdv-haric-amount'),
      kdvDahil: document.getElementById('kdv-dahil-amount'),
      kdvTutari: document.getElementById('kdv-tutari'),
      appliedRate: document.getElementById('applied-rate')
    };

    // Current state
    this.currentRate = 20; // Default %20
    this.isCustomRate = false;

    this.init();
  }

  init() {
    if (!this.form) return;

    this.bindEvents();
    this.setupRealTimeCalculation();
    this.initializeDefaultState();
  }

  initializeDefaultState() {
    // Set default radio button selection
    if (this.calculationDirection.dahilToHaric) {
      this.calculationDirection.dahilToHaric.checked = true;
    }
    
    // Set default rate button
    const defaultRateBtn = document.querySelector('.kdv-rate-btn[data-rate="20"]');
    if (defaultRateBtn) {
      defaultRateBtn.classList.add('active');
    }
  }

  bindEvents() {
    // Calculate button
    this.calculateBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.calculateKdv();
    });

    // Reset button
    this.resetBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.resetForm();
    });


    // Radio button changes
    Object.values(this.calculationDirection).forEach(radio => {
      if (radio) {
        radio.addEventListener('change', () => {
          this.handleDirectionChange();
        });
      }
    });

    // VAT rate buttons
    this.rateButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleRateSelection(btn);
      });
    });

    // Form validation on input
    Object.values(this.inputs).forEach(input => {
      if (input) {
        input.addEventListener('input', () => {
          this.validateInput(input);
        });

        input.addEventListener('blur', () => {
          this.validateInput(input);
        });
      }
    });

    // Enter key submission
    this.form.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.calculateKdv();
      }
    });
  }

  setupRealTimeCalculation() {
    // Calculate as user types (with debouncing)
    let timeout;
    const triggerCalculation = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        if (this.isFormValid(false)) {
          this.calculateKdv(true); // Silent calculation
        }
      }, 500);
    };

    // Amount input
    if (this.inputs.amount) {
      this.inputs.amount.addEventListener('input', triggerCalculation);
    }

    // Custom rate input
    if (this.inputs.customRate) {
      this.inputs.customRate.addEventListener('input', triggerCalculation);
    }
  }

  handleDirectionChange() {
    // Update UI labels or placeholder text if needed
    const isKdvDahil = this.calculationDirection.dahilToHaric?.checked;
    const amountInput = this.inputs.amount;
    
    if (amountInput) {
      if (isKdvDahil) {
        amountInput.placeholder = 'KDV Dahil tutar girin';
      } else {
        amountInput.placeholder = 'KDV Hariç tutar girin';
      }
    }

    // Recalculate if form is valid
    if (this.isFormValid(false)) {
      this.calculateKdv(true);
    }
  }

  handleRateSelection(selectedBtn) {
    // Remove active class from all buttons
    this.rateButtons.forEach(btn => btn.classList.remove('active'));
    
    // Add active class to selected button
    selectedBtn.classList.add('active');
    
    const rate = selectedBtn.getAttribute('data-rate');
    
    if (rate === 'custom') {
      this.isCustomRate = true;
      this.showCustomRateInput();
      // Use custom rate input value
      const customValue = parseFloat(this.inputs.customRate?.value) || 0;
      this.currentRate = customValue;
    } else {
      this.isCustomRate = false;
      this.hideCustomRateInput();
      this.currentRate = parseFloat(rate);
    }

    // Recalculate if form is valid
    if (this.isFormValid(false)) {
      this.calculateKdv(true);
    }
  }

  showCustomRateInput() {
    if (this.customRateContainer) {
      this.customRateContainer.style.display = 'block';
      this.customRateContainer.classList.add('show');
      
      // Focus custom rate input
      setTimeout(() => {
        if (this.inputs.customRate) {
          this.inputs.customRate.focus();
        }
      }, 100);
    }
  }

  hideCustomRateInput() {
    if (this.customRateContainer) {
      this.customRateContainer.style.display = 'none';
      this.customRateContainer.classList.remove('show');
    }
  }

  validateInput(input) {
    const value = parseFloat(input.value);
    const field = input.closest('.form-field');
    
    // Remove previous error states
    input.classList.remove('error');
    field?.classList.remove('error');

    // Validate amount input
    if (input === this.inputs.amount) {
      if (input.hasAttribute('required') && (!input.value || value <= 0)) {
        input.classList.add('error');
        field?.classList.add('error');
        return false;
      }

      if (input.value && (isNaN(value) || value < 0)) {
        input.classList.add('error');
        field?.classList.add('error');
        return false;
      }
    }

    // Validate custom rate input
    if (input === this.inputs.customRate && this.isCustomRate) {
      if (!input.value || isNaN(value) || value < 0 || value > 100) {
        input.classList.add('error');
        field?.classList.add('error');
        return false;
      }
      
      // Update current rate
      this.currentRate = value;
    }

    return true;
  }

  isFormValid(showErrors = true) {
    let isValid = true;

    // Validate amount
    if (this.inputs.amount) {
      const amountValid = this.validateInput(this.inputs.amount);
      if (!amountValid) {
        isValid = false;
      }
    }

    // Validate custom rate if applicable
    if (this.isCustomRate && this.inputs.customRate) {
      const customRateValid = this.validateInput(this.inputs.customRate);
      if (!customRateValid) {
        isValid = false;
      }
    }

    // Check if a valid rate is selected
    if (!this.isCustomRate && (!this.currentRate || this.currentRate <= 0)) {
      isValid = false;
    }

    return isValid;
  }

  calculateKdv(silent = false) {
    if (!this.isFormValid()) {
      if (!silent) {
        this.showError('Lütfen tüm alanları doğru şekilde doldurun.');
      }
      return;
    }

    // Show loading state
    if (!silent) {
      this.calculateBtn.classList.add('loading');
    }

    // Get values
    const amount = parseFloat(this.inputs.amount.value) || 0;
    const rate = this.currentRate / 100; // Convert percentage to decimal
    const isKdvDahil = this.calculationDirection.dahilToHaric?.checked;

    let kdvHaric, kdvDahil, kdvTutari;

    if (isKdvDahil) {
      // KDV Dahil → KDV Hariç calculation
      kdvDahil = amount;
      kdvHaric = amount / (1 + rate);
      kdvTutari = kdvDahil - kdvHaric;
    } else {
      // KDV Hariç → KDV Dahil calculation
      kdvHaric = amount;
      kdvDahil = amount * (1 + rate);
      kdvTutari = kdvDahil - kdvHaric;
    }

    // Update results
    setTimeout(() => {
      this.displayResults({
        kdvHaric: kdvHaric,
        kdvDahil: kdvDahil,
        kdvTutari: kdvTutari,
        appliedRate: this.currentRate
      });

      if (!silent) {
        this.calculateBtn.classList.remove('loading');
        this.showResult();
      }
    }, silent ? 0 : 300);
  }

  displayResults(results) {
    if (this.resultElements.kdvHaric) {
      this.resultElements.kdvHaric.textContent = 
        this.formatCurrency(results.kdvHaric);
    }

    if (this.resultElements.kdvDahil) {
      this.resultElements.kdvDahil.textContent = 
        this.formatCurrency(results.kdvDahil);
    }

    if (this.resultElements.kdvTutari) {
      this.resultElements.kdvTutari.textContent = 
        this.formatCurrency(results.kdvTutari);
    }

    if (this.resultElements.appliedRate) {
      this.resultElements.appliedRate.textContent = 
        `%${results.appliedRate}`;
    }
  }

  formatCurrency(amount) {
    return `${amount.toFixed(2)} ₺`;
  }

  showResult() {
    if (this.resultSection) {
      this.resultSection.style.display = 'block';
      this.resultSection.classList.add('show');
      
      // Smooth scroll to result
      setTimeout(() => {
        this.resultSection.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest' 
        });
      }, 100);
    }
  }

  hideResult() {
    if (this.resultSection) {
      this.resultSection.style.display = 'none';
      this.resultSection.classList.remove('show');
    }
  }

  resetForm() {
    // Clear all inputs
    Object.values(this.inputs).forEach(input => {
      if (input) {
        input.value = '';
        input.classList.remove('error');
        const field = input.closest('.form-field');
        field?.classList.remove('error');
      }
    });

    // Reset radio buttons to default
    if (this.calculationDirection.dahilToHaric) {
      this.calculationDirection.dahilToHaric.checked = true;
    }

    // Reset rate selection to default (20%)
    this.rateButtons.forEach(btn => btn.classList.remove('active'));
    const defaultRateBtn = document.querySelector('.kdv-rate-btn[data-rate="20"]');
    if (defaultRateBtn) {
      defaultRateBtn.classList.add('active');
    }
    
    this.currentRate = 20;
    this.isCustomRate = false;
    this.hideCustomRateInput();

    // Hide result
    this.hideResult();

    // Focus first input
    if (this.inputs.amount) {
      this.inputs.amount.focus();
    }
  }




  showError(message) {
    // You can implement a toast/alert system here
    // For now, using browser alert
    alert(message);
  }
}

// Utility function to format numbers
function formatNumber(num, decimals = 2) {
  return parseFloat(num).toFixed(decimals);
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new KdvCalculator();
});

// Re-initialize for theme editor
document.addEventListener('shopify:section:load', (event) => {
  if (event.detail.sectionId.includes('kdv-calculator')) {
    new KdvCalculator();
  }
});

// Handle theme editor unload
document.addEventListener('shopify:section:unload', (event) => {
  if (event.detail.sectionId.includes('kdv-calculator')) {
    // Cleanup if needed
  }
});